/**
 * Builds ActiveFilter[] from the server-parsed `initialFilters` value
 * (pageContext.data.initialFilters, produced by normalizeFiltersFromUrl in +data.ts).
 *
 * Using this source instead of re-parsing the URL ensures:
 * - ALL filter types are captured (not just the 4 the old code handled)
 * - Values are already normalised/capitalised to match Algolia facet values
 * - Both flat (?remoteness=onsite) and namespaced (?en_search[refinementList][remoteness][0]=onsite) URLs work
 */

import type { ActiveFilter, FilterType } from '../../types/srp-seo-meta'

/** Maps Algolia attribute names → SEO FilterType */
const ATTRIBUTE_TO_FILTER_TYPE: Record<string, FilterType> = {
  location: 'location',
  companyId: 'company',
  field: 'field',
  remoteness: 'remoteness',
  requiredLanguages: 'requiredLanguages',
  jobType: 'jobType',
  normalisedRoleLevel: 'normalisedRoleLevel',
  companyIndustry: 'companyIndustry',
  educationLevel: 'educationLevel',
  benefits: 'benefits',
  skillsAndTechnologies: 'skillsAndTechnologies',
  peopleManagementTier: 'peopleManagementTier',
  specialisations: 'specialisations',
}

/**
 * Extracts the Algolia attribute name from either a plain key ("remoteness")
 * or a namespaced key ("en_search[refinementList][remoteness][0]").
 */
function extractAttribute(key: string): string {
  const match = key.match(/\[refinementList\]\[([^\]]+)\]/)
  return match ? match[1] : key
}

function parseFiltersObject(raw: unknown): Record<string, string[]> {
  if (!raw) return {}
  if (typeof raw === 'string') {
    if (!raw.trim()) return {}
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
    } catch {
      return {}
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, string[]>
  }
  return {}
}

/**
 * Converts the server-normalised `initialFilters` map into an `ActiveFilter[]`
 * suitable for `generateSEOMeta`. One ActiveFilter is emitted per filter type;
 * multi-value selections use the first value only (most important for titles).
 */
export function buildActiveFiltersFromInitialFilters(initialFilters: unknown): ActiveFilter[] {
  const filtersObj = parseFiltersObject(initialFilters)
  const filters: ActiveFilter[] = []
  const seen = new Set<FilterType>()

  for (const [rawKey, values] of Object.entries(filtersObj)) {
    if (!Array.isArray(values) || values.length === 0) continue

    const attribute = extractAttribute(rawKey)
    const filterType = ATTRIBUTE_TO_FILTER_TYPE[attribute]
    if (!filterType || seen.has(filterType)) continue

    const value = String(values[0])
    if (!value) continue

    seen.add(filterType)
    filters.push({
      type: filterType,
      value,
      rawValue: filterType === 'requiredLanguages' ? value.toLowerCase() : value,
    })
  }

  return filters
}
